'use client';

import type { UserProgress } from '@repo/lib';
import { createContext, useContext } from 'react';
import { useUserProgressSource } from '@/hooks/use-user-progress-source';
import { queryClient } from '@/lib/trpc';

type UserContextType = {
  userProgress: UserProgress | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  progressError: unknown | null;
  profileMissing: boolean;
  shouldPromptOnboarding: boolean;
  refreshUserProgress: () => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const {
    userProgress,
    isLoading,
    isLoggedIn,
    progressError,
    profileMissing,
    shouldPromptOnboarding,
  } = useUserProgressSource();

  const refreshUserProgress = () => {
    queryClient.invalidateQueries({ queryKey: ['user', 'progress'] });
  };

  const value: UserContextType = {
    userProgress,
    isLoading,
    isLoggedIn,
    progressError,
    profileMissing,
    shouldPromptOnboarding,
    refreshUserProgress,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
